import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../Education.css';

const Intermediate: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    }
  };

  const modules = [
    {
      title: 'Advanced Chart Patterns',
      topics: [
        'Complex reversal patterns (Head & Shoulders, Double Tops/Bottoms)',
        'Continuation patterns (Flags, Pennants, Wedges)',
        'Harmonic patterns (Gartley, Butterfly, Bat)',
        'Fibonacci retracements and extensions',
        'Elliott Wave Theory basics',
        'Pattern failure and false breakouts'
      ],
      duration: '5-6 hours'
    },
    {
      title: 'Indicator Applications',
      topics: [
        'Moving Average strategies (crossovers, ribbons)',
        'Oscillators (RSI, Stochastic, MACD)',
        'Volume indicators (OBV, Volume Profile)',
        'Volatility indicators (Bollinger Bands, ATR)',
        'Combining multiple indicators effectively',
        'Avoiding indicator overload'
      ],
      duration: '4-5 hours'
    },
    {
      title: 'Trading Psychology',
      topics: [
        'Understanding fear and greed in trading',
        'Overcoming revenge trading',
        'Developing discipline and patience',
        'Managing winning and losing streaks',
        'Creating and following a trading plan',
        'Meditation and mental preparation techniques'
      ],
      duration: '3-4 hours'
    },
    {
      title: 'Money Management',
      topics: [
        'Advanced position sizing techniques',
        'Risk-adjusted returns and Sharpe ratio',
        'Portfolio diversification strategies',
        'Correlation between different assets',
        'Drawdown management and recovery',
        'Compounding and growth strategies'
      ],
      duration: '4-5 hours'
    }
  ];

  const tradingStrategies = [
    { name: 'Swing Trading', description: 'Hold positions for days to weeks, capturing larger price swings' },
    { name: 'Breakout Trading', description: 'Enter positions when price breaks key support/resistance levels' },
    { name: 'Range Trading', description: 'Trade between established support and resistance in sideways markets' },
    { name: 'Trend Following', description: 'Identify and ride established trends using multiple timeframe analysis' },
    { name: 'Pullback Trading', description: 'Enter trends during temporary retracements for better risk/reward' },
    { name: 'News Trading', description: 'Capitalize on volatility around major economic announcements' }
  ];

  return (
    <motion.div 
      className="education-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="page-content">
        <motion.div className="page-title" variants={itemVariants}>
          <h1>Intermediate Trading Course</h1>
          <p>Develop advanced skills and create your trading edge</p>
        </motion.div>

        <motion.section className="course-overview" variants={itemVariants}>
          <h2>Course Overview</h2>
          <p>
            The Intermediate course is designed for traders who understand the basics and are ready to develop more 
            sophisticated trading strategies. You'll learn advanced technical analysis, psychology, and money management 
            techniques used by professional traders.
          </p>
          <div className="prerequisites-box">
            <h3>📋 Prerequisites</h3>
            <ul>
              <li>Completed Beginner course or equivalent knowledge</li>
              <li>At least 3-6 months of demo trading experience</li>
              <li>Understanding of basic technical analysis</li>
              <li>Familiarity with a trading platform</li>
            </ul>
          </div>
          <div className="course-stats">
            <div className="stat">
              <h3>16-20 Hours</h3>
              <p>Total Course Duration</p>
            </div>
            <div className="stat">
              <h3>4 Modules</h3>
              <p>Advanced Content</p>
            </div>
            <div className="stat">
              <h3>Live Sessions</h3>
              <p>Q&A with Instructors</p>
            </div>
            <div className="stat">
              <h3>Trading Lab</h3>
              <p>Practice Exercises</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="learning-path" variants={itemVariants}>
          <h2>Course Curriculum</h2>
          <div className="modules-list">
            {modules.map((module, index) => (
              <motion.div 
                key={index} 
                className="module-card"
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
              >
                <div className="module-header">
                  <span className="module-number">Module {index + 1}</span>
                  <span className="module-duration">⏱️ {module.duration}</span>
                </div>
                <h3>{module.title}</h3>
                <ul className="topics-list">
                  {module.topics.map((topic, topicIndex) => (
                    <li key={topicIndex}>{topic}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="trading-strategies-section" variants={itemVariants}>
          <h2>Trading Strategies You'll Master</h2>
          <div className="strategies-grid">
            {tradingStrategies.map((strategy, index) => (
              <motion.div 
                key={index} 
                className="strategy-card"
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
              >
                <h3>📊 {strategy.name}</h3>
                <p>{strategy.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="course-benefits" variants={itemVariants}>
          <h2>What Sets This Course Apart</h2>
          <div className="benefits-grid">
            <div className="benefit">
              <h3>🎯 Real Market Analysis</h3>
              <p>Live market analysis sessions to apply concepts in real-time</p>
            </div>
            <div className="benefit">
              <h3>📊 Strategy Development</h3>
              <p>Learn to create and backtest your own trading strategies</p>
            </div>
            <div className="benefit">
              <h3>🧠 Psychology Focus</h3>
              <p>Deep dive into the mental aspects that separate winners from losers</p>
            </div>
            <div className="benefit">
              <h3>💰 Money Management</h3>
              <p>Professional-level portfolio and risk management techniques</p>
            </div>
            <div className="benefit">
              <h3>👥 Community Access</h3>
              <p>Join our trading community for ongoing support and discussion</p>
            </div>
            <div className="benefit">
              <h3>📈 Trading Plan Template</h3>
              <p>Receive a professional trading plan template to customize</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="skills-development" variants={itemVariants}>
          <h2>Skills You'll Develop</h2>
          <div className="skills-list">
            <div className="skill-item">
              <h4>✓ Pattern Recognition Mastery</h4>
              <p>Quickly identify and act on chart patterns across multiple timeframes</p>
            </div>
            <div className="skill-item">
              <h4>✓ Multi-Timeframe Analysis</h4>
              <p>Analyze trends across different timeframes for better entries</p>
            </div>
            <div className="skill-item">
              <h4>✓ Risk-Reward Optimization</h4>
              <p>Calculate and optimize risk-reward ratios for maximum profitability</p>
            </div>
            <div className="skill-item">
              <h4>✓ Emotional Control</h4>
              <p>Manage emotions and stick to your trading plan under pressure</p>
            </div>
            <div className="skill-item">
              <h4>✓ Strategy Backtesting</h4>
              <p>Test and validate trading strategies using historical data</p>
            </div>
            <div className="skill-item">
              <h4>✓ Market Correlation Analysis</h4>
              <p>Understand how different markets and assets correlate</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="next-steps" variants={itemVariants}>
          <h2>Your Growth Path</h2>
          <p>
            After completing the Intermediate course, you'll have developed a solid trading methodology and 
            psychological framework. You'll be ready to trade live with real money (starting small) or advance 
            to our Advanced course to learn algorithmic trading and professional-level optimization techniques.
          </p>
          <div className="progression-path">
            <div className="path-step completed">
              <h4>✓ Beginner</h4>
              <p>Foundation Built</p>
            </div>
            <div className="path-arrow">→</div>
            <div className="path-step current">
              <h4>✓ Intermediate</h4>
              <p>Skills Developed</p>
            </div>
            <div className="path-arrow">→</div>
            <div className="path-step">
              <h4>Advanced</h4>
              <p>Master Trading</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="success-metrics" variants={itemVariants}>
          <h2>Measuring Your Success</h2>
          <div className="metrics-grid">
            <div className="metric">
              <h3>Win Rate</h3>
              <p>Track your percentage of winning trades</p>
            </div>
            <div className="metric">
              <h3>Risk/Reward Ratio</h3>
              <p>Ensure you're targeting at least 1:2 or better</p>
            </div>
            <div className="metric">
              <h3>Maximum Drawdown</h3>
              <p>Monitor your largest peak-to-trough decline</p>
            </div>
            <div className="metric">
              <h3>Expectancy</h3>
              <p>Calculate your average profit per trade over time</p>
            </div>
          </div>
        </motion.section>

        <motion.div className="cta-section" variants={itemVariants}>
          <h2>Take Your Trading to the Next Level</h2>
          <p>Master advanced techniques and develop your edge in the markets</p>
          <div className="btn-container">
            <Link to="/contact" className="btn">Enroll in Intermediate</Link>
            <Link to="/education/advanced" className="btn secondary">View Advanced Course</Link>
            <Link to="/services/signals" className="btn secondary">Get Trading Signals</Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Intermediate;
